import React from 'react';
import { Box, IconButton, Tooltip, Typography, Stack, Chip, LinearProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PendingIcon from '@mui/icons-material/Pending';
import TaskAltIcon from '@mui/icons-material/TaskAlt';

interface RuleCardProps {
  complete: boolean;
  accepted: number;
  completed: number;
  children: React.ReactNode;
  ticketsGoal?: number; // Optional for showing progress towards goal
}

/**
 * Component to display a parameter rule with a completion status indicator
 */
export default function RuleCard({ complete, accepted, completed, ticketsGoal, children }: RuleCardProps) {
  // Calculate progress percentages for the progress bar
  const totalProgress = ticketsGoal ? Math.min(100, ((accepted + completed) * 100) / ticketsGoal) : 0;
  const completedProgress = ticketsGoal ? Math.min(100, (completed * 100) / ticketsGoal) : 0;
  
  return (
    <Box position="relative" p={2} border="1px solid" borderColor={complete ? 'success.light' : 'grey.300'} borderRadius={1} mb={2}>
      <Box
        sx={{
          position: 'absolute',
          top: 4,
          right: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        {/* Status icon */}
        <Tooltip title={complete ? 'Rule complete' : 'Rule in progress'}>
          <IconButton size="small">
            {complete ? (
              <CheckCircleIcon fontSize="small" color="success" />
            ) : (
              <HourglassEmptyIcon fontSize="small" color="warning" />
            )}
          </IconButton>
        </Tooltip>
      </Box>
      
      {/* Progress bar section */}
      <Box mt={1} mb={3} px={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Typography variant="caption" color="text.secondary">
            Progress: {accepted + completed} / {ticketsGoal || 0} tickets
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {totalProgress.toFixed(0)}%
          </Typography>
        </Box>
        
        <Tooltip 
          title={
            <Box sx={{ p: 0.5 }}>

              <Box display="flex" alignItems="center">
                <Box 
                  sx={{ 
                    width: 12, 
                    height: 12, 
                    backgroundColor: 'success.main', 
                    mr: 1, 
                    borderRadius: '2px' 
                  }} 
                />
                <Typography variant="caption" color="white">
                  {completed} tickets completed
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" mb={0.5}>
                <Box 
                  sx={{ 
                    width: 12, 
                    height: 12, 
                    backgroundColor: 'primary.main', 
                    mr: 1, 
                    borderRadius: '2px' 
                  }} 
                />
                <Typography variant="caption" color="white">
                  {accepted} tickets accepted by AutoBuy
                </Typography>
              </Box>
            </Box>
          }
          arrow
          placement="top"
        >
          <Box sx={{ position: 'relative', pt: 0.5 }}>
            {/* Base progress bar (accepted + completed) */}
            <LinearProgress 
              variant="determinate" 
              value={totalProgress} 
              sx={{ 
                height: 8, 
                borderRadius: 1,
                backgroundColor: 'grey.200',
                cursor: 'pointer'
              }} 
            />
          
            {/* Completed progress overlay */}
            <LinearProgress 
              variant="determinate" 
              value={completedProgress} 
              sx={{ 
                position: 'absolute',
                top: '4px',
                left: 0,
                right: 0,
                height: 8, 
                borderRadius: 1,
                backgroundColor: 'transparent',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'success.main'
                }
              }} 
            />
          </Box>
        </Tooltip>
      </Box>
      
      {/* Content */}
      <Box>
        {children}
      </Box>
    </Box>
  );
}
